import React, { useEffect, useState } from "react";
import logger from "../../logger";
import {
  Button,
  Text,
  MessageBar,
  MessageBarBody,
  Dropdown,
  Option,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowSyncRegular,
  AddRegular,
  DeleteRegular,
  TableAddRegular,
} from "@fluentui/react-icons";
import { fetchRubrics, fetchScorecardsByRubric } from "./scorecardService";
import {
  insertScorecardDataToExcel,
  insertAllScorecardsToExcel,
  clearScorecardDataRange,
} from "./scorecardExcelHelpers";
import useScorecardFilters from "./useScorecardFilters";
import { useScorecardManagementStyles } from "./ScorecardManagement.styles";

const ScorecardManagement = () => {
  const [excelData, setExcelData] = useState("");
  const [excelError, setExcelError] = useState(false);

  const [rubrics, setRubrics] = useState([]);
  const [selectedRubricId, setSelectedRubricId] = useState("");
  const [rubricsLoading, setRubricsLoading] = useState(false);

  const [allScorecards, setAllScorecards] = useState([]);
  const [scorecardsLoading, setScorecardsLoading] = useState(false);

  const [scorecardDataRange, setScorecardDataRange] = useState(null);
  const styles = useScorecardManagementStyles();

  const {
    selectedPeriod,
    selectedCompanyId,
    selectedScorecardId,
    setSelectedScorecardId,
    availablePeriods,
    availableCompanies,
    availableReports,
    handlePeriodSelect,
    handleCompanySelect,
    resetFilters,
  } = useScorecardFilters(allScorecards);

  const selectedRubricDisplayName = React.useMemo(() => {
    if (!selectedRubricId) return "";
    const rubric = rubrics.find((r) => r.id === selectedRubricId);
    return rubric ? rubric.name || rubric.id : "";
  }, [selectedRubricId, rubrics]);

  const selectedCompanyDisplayName = React.useMemo(() => {
    if (!selectedCompanyId) return "";
    const company = availableCompanies.find((c) => c.id === selectedCompanyId);
    return company ? company.name : "";
  }, [selectedCompanyId, availableCompanies]);

  const selectedReportDisplayName = React.useMemo(() => {
    if (!selectedScorecardId) return "";
    const report = availableReports.find((r) => r.scorecardId === selectedScorecardId);
    return report ? report.displayName : "";
  }, [selectedScorecardId, availableReports]);

  const showMessage = (message, isError = false) => {
    setExcelData(message);
    setExcelError(isError);
    setTimeout(() => setExcelData(""), 3000);
  };

  useEffect(() => {
    loadRubrics();
  }, []);

  const loadRubrics = async () => {
    try {
      setRubricsLoading(true);
      const rubricsData = await fetchRubrics();
      setRubrics(rubricsData);
    } catch (error) {
      logger.error("Error loading rubrics:", error);
      showMessage("Error loading rubrics: " + (error.message || "Unknown error"), true);
    } finally {
      setRubricsLoading(false);
    }
  };

  const loadScorecardsForRubric = async (rubricId) => {
    try {
      setScorecardsLoading(true);
      const { scorecards } = await fetchScorecardsByRubric(rubricId);
      setAllScorecards(scorecards);
    } catch (error) {
      logger.error("Error loading scorecards:", error);
      showMessage("Error loading scorecards: " + (error.message || "Unknown error"), true);
    } finally {
      setScorecardsLoading(false);
    }
  };

  const handleRubricSelect = async (rubricId) => {
    setSelectedRubricId(rubricId);
    resetFilters();
    setAllScorecards([]);

    if (rubricId) {
      await loadScorecardsForRubric(rubricId);
    }
  };

  const handleInsertScorecard = async () => {
    if (!selectedScorecardId) {
      showMessage("Please complete all selections first", true);
      return;
    }

    const scorecard = allScorecards.find((sc) => sc.id === selectedScorecardId);

    if (!scorecard) {
      showMessage("Scorecard not found.", true);
      return;
    }

    try {
      const rangeInfo = await insertScorecardDataToExcel(scorecard, selectedRubricDisplayName);
      setScorecardDataRange(rangeInfo);
      showMessage("Scorecard data inserted successfully!");
    } catch (error) {
      logger.error("Error inserting scorecard data to Excel:", error);
      showMessage("Error inserting scorecard data: " + (error.message || "Unknown error"), true);
    }
  };

  const handleInsertAllScorecards = async () => {
    if (!allScorecards.length) {
      showMessage("No scorecards available to insert", true);
      return;
    }

    try {
      const rangeInfo = await insertAllScorecardsToExcel(allScorecards, selectedRubricDisplayName);
      setScorecardDataRange(rangeInfo);
      showMessage(`${allScorecards.length} scorecard(s) inserted successfully!`);
    } catch (error) {
      logger.error("Error inserting all scorecards to Excel:", error);
      showMessage("Error inserting scorecards: " + (error.message || "Unknown error"), true);
    }
  };

  const handleClearAll = async () => {
    setSelectedRubricId("");
    resetFilters();
    setAllScorecards([]);

    try {
      await clearScorecardDataRange(scorecardDataRange);
      setScorecardDataRange(null);
      showMessage("Scorecard data cleared successfully!");
    } catch (error) {
      logger.error("Error clearing scorecard data:", error);
      showMessage("Error clearing scorecard data: " + (error.message || "Unknown error"), true);
    }
  };

  const isLoading = rubricsLoading || scorecardsLoading;

  return (
    <div className={styles.container}>
      {/* 1. Rubric */}
      <div className={styles.dropdownContainer}>
        <Text as="p" className={styles.label}>
          1. Select a Rubric:
        </Text>
        <Dropdown
          positioning="below-start"
          inlinePopup
          placeholder={rubricsLoading ? "Loading rubrics..." : "Select a rubric"}
          selectedOptions={selectedRubricDisplayName ? [selectedRubricDisplayName] : []}
          value={selectedRubricDisplayName}
          disabled={rubricsLoading}
          onOptionSelect={(event, data) => {
            const selectedName = data.optionValue || "";
            const rubric = rubrics.find((r) => (r.name || r.id) === selectedName);
            handleRubricSelect(rubric?.id || "");
          }}
          className={styles.dropdown}
        >
          {rubrics.map((rubric) => {
            const name = rubric.name || rubric.id;
            return (
              <Option key={rubric.id} value={name}>
                {name}
              </Option>
            );
          })}
        </Dropdown>
      </div>

      {/* 2. Reporting Period */}
      <div className={styles.dropdownContainer}>
        <Text as="p" className={styles.label}>
          2. Select Reporting Period:
        </Text>
        <Dropdown
          positioning="below-start"
          inlinePopup
          placeholder={
            scorecardsLoading
              ? "Loading..."
              : !selectedRubricId
                ? "Select a rubric first"
                : availablePeriods.length === 0
                  ? "No reporting periods available"
                  : "Select a reporting period"
          }
          selectedOptions={selectedPeriod ? [selectedPeriod] : []}
          value={selectedPeriod}
          disabled={!selectedRubricId || scorecardsLoading || availablePeriods.length === 0}
          onOptionSelect={(event, data) => {
            handlePeriodSelect(data.optionValue || "");
          }}
          className={styles.dropdown}
        >
          {availablePeriods.map((period) => (
            <Option key={period} value={period}>
              {period}
            </Option>
          ))}
        </Dropdown>
      </div>

      {/* 3. Company */}
      <div className={styles.dropdownContainer}>
        <Text as="p" className={styles.label}>
          3. Select a Company:
        </Text>
        <Dropdown
          positioning="below-start"
          inlinePopup
          placeholder={
            !selectedPeriod
              ? "Select a reporting period first"
              : availableCompanies.length === 0
                ? "No companies available"
                : "Select a company"
          }
          selectedOptions={selectedCompanyDisplayName ? [selectedCompanyDisplayName] : []}
          value={selectedCompanyDisplayName}
          disabled={!selectedPeriod || availableCompanies.length === 0}
          onOptionSelect={(event, data) => {
            const selectedName = data.optionValue || "";
            const company = availableCompanies.find((c) => c.name === selectedName);
            handleCompanySelect(company?.id || "");
          }}
          className={styles.dropdown}
        >
          {availableCompanies.map((company) => (
            <Option key={company.id} value={company.name}>
              {company.name}
            </Option>
          ))}
        </Dropdown>
      </div>

      {/* 4. Report */}
      <div className={styles.dropdownContainer}>
        <Text as="p" className={styles.label}>
          4. Select a Report:
        </Text>
        <Dropdown
          positioning="below-start"
          inlinePopup
          placeholder={
            !selectedCompanyId
              ? "Select a company first"
              : availableReports.length === 0
                ? "No reports available"
                : "Select a report"
          }
          selectedOptions={selectedReportDisplayName ? [selectedReportDisplayName] : []}
          value={selectedReportDisplayName}
          disabled={!selectedCompanyId || availableReports.length === 0}
          onOptionSelect={(event, data) => {
            const selectedName = data.optionValue || "";
            const report = availableReports.find((r) => r.displayName === selectedName);
            setSelectedScorecardId(report?.scorecardId || "");
          }}
          className={styles.dropdown}
        >
          {availableReports.map((report) => (
            <Option key={report.scorecardId} value={report.displayName}>
              {report.displayName}
            </Option>
          ))}
        </Dropdown>
      </div>

      <Text
        as="p"
        style={{
          fontSize: "12px",
          color: "#605e5c",
          fontStyle: "italic",
          marginTop: "-8px",
          marginBottom: "12px",
        }}
      >
        Note: Select a rubric first. Each subsequent dropdown is filtered to only show options with
        existing scorecards.
      </Text>

      <div className={styles.buttonsContainer}>
        <Tooltip
          withArrow
          content={{ children: "Refetch Data", className: styles.tooltip }}
          relationship="label"
          positioning="below"
        >
          <Button
            appearance="secondary"
            onClick={() => {
              loadRubrics();
              if (selectedRubricId) {
                loadScorecardsForRubric(selectedRubricId);
              }
            }}
            disabled={isLoading}
            className={styles.refetchButton}
            icon={<ArrowSyncRegular />}
          />
        </Tooltip>
        <Tooltip
          withArrow
          content={{ children: "Insert Scorecard", className: styles.tooltip }}
          relationship="label"
          positioning="below"
        >
          <Button
            appearance="primary"
            onClick={handleInsertScorecard}
            disabled={isLoading || !selectedScorecardId}
            className={styles.insertButton}
            icon={<AddRegular />}
          />
        </Tooltip>
        {allScorecards.length > 0 && (
          <Tooltip
            withArrow
            content={{ children: "Insert All Scorecards", className: styles.tooltip }}
            relationship="label"
            positioning="below"
          >
            <Button
              appearance="primary"
              onClick={handleInsertAllScorecards}
              disabled={isLoading}
              className={styles.insertAllButton}
              icon={<TableAddRegular />}
            />
          </Tooltip>
        )}
        <Tooltip
          withArrow
          content={{ children: "Clear Scorecard Data", className: styles.tooltip }}
          relationship="label"
          positioning="below"
        >
          <Button
            appearance="secondary"
            onClick={handleClearAll}
            disabled={isLoading}
            className={styles.clearButton}
            icon={<DeleteRegular />}
          />
        </Tooltip>
      </div>

      {excelData && (
        <MessageBar
          intent={excelError ? "error" : "success"}
          className={excelError ? styles.errorMessage : styles.successMessage}
        >
          <MessageBarBody>{excelData}</MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
};

export default ScorecardManagement;
